/**
 * FullFinancialAuditTabs — Johan-only bank-statement view (v4.7.52).
 * Composes the data returned by /api/admin/full-financial-audit into a 5-tab
 * layout: Statement (chronological ledger) · Shares · Commissions Earned ·
 * Commissions Generated (paid to uplines) · Referrer.
 *
 * Auto-hide contract: any tab whose underlying dataset has zero rows is not
 * rendered (operator request: "if the user has no active data within a certain
 * transaction then don't add it to the statement. THIS MUST BE AN AUTOMATIC TASK").
 * The Statement tab is always shown so the running balance is reachable even
 * when individual blocks are empty.
 *
 * Running balance contract: walks USDT and shares forward from 0 at the user's
 * signup date. Only terminal-success statuses move the balance; informational
 * rows (pending / cancelled / reversed / rejected / failed) are still rendered
 * but flagged and do not affect the cumulative columns.
 */
import _React, { useMemo, useState } from 'react';
import {
  fmt, dt, card, h, lbl, tableStyle, th, td,
  StatusPill, Section, Empty, ScrollWrap, CounterpartyCell,
} from './FullFinancialAuditSections';
import { buildAccountLedger } from '../../lib/accountLedger';

const TERMINAL = {
  share_purchase: new Set(['approved', 'active', 'consolidated', 'completed', 'paid']),
  commission: new Set(['paid', 'completed', 'approved']),
  conversion: new Set(['approved']),
  withdrawal: new Set(['paid', 'completed']),
  transfer: new Set(['completed']),
};

const norm = (s: any) => String(s ?? '').trim().toLowerCase();

function kindBadge(kind: string) {
  const map: Record<string, { bg: string; label: string }> = {
    share_purchase: { bg: '#1D4ED8', label: 'Purchase' },
    commission_shares: { bg: '#2563EB', label: 'Comm. shares' },
    commission_topup: { bg: '#7C3AED', label: 'Topup' },
    commission_earned: { bg: '#047857', label: 'Earned' },
    commission_conversion: { bg: '#B45309', label: 'Convert' },
    withdrawal: { bg: '#B91C1C', label: 'Withdraw' },
    transfer_sent: { bg: '#9F1239', label: 'Tx Sent' },
    transfer_received: { bg: '#0E7490', label: 'Tx Recv' },
    legacy_shares: { bg: '#4B5563', label: 'Legacy' },
    reconciliation: { bg: '#92400E', label: 'Adjustment' },
  };
  const m = map[kind] || { bg: '#374151', label: kind };
  return <span style={{ backgroundColor: m.bg, color: '#FFF', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{m.label}</span>;
}

function signed(n: number, d = 2) { if (!n) return '0.00'; return (n > 0 ? '+' : '') + fmt(n, d); }
function signColor(n: number) { if (n > 0) return '#86EFAC'; if (n < 0) return '#FCA5A5'; return '#9CA3AF'; }

interface TabsProps { data: any; }

const TAB_KEYS = ['statement', 'shares', 'earned', 'generated', 'referrer'] as const;
type TabKey = typeof TAB_KEYS[number];

function hasShares(data: any) {
  const sp = data?.share_purchases || {}; const st = data?.share_transfers || {}; const cc = data?.commission_conversions || {};
  return (sp.all?.length || 0) + (st.sent?.length || 0) + (st.received?.length || 0) + (cc.rows?.length || 0) > 0;
}
function hasEarned(data: any) {
  const ce = data?.commissions_earned || {};
  return (ce.commission_transactions?.length || 0) + (ce.multi_level_commissions?.length || 0) > 0;
}
function hasGenerated(data: any) {
  const cpo = data?.commissions_paid_to_others || {};
  return (cpo.commission_transactions?.length || 0) + (cpo.multi_level_commissions?.length || 0) > 0;
}
function hasReferrer(data: any) { return !!data?.referrer; }


function StatementTab({ data }: TabsProps) {
  const L = useMemo(() => buildAccountLedger(data), [data]);
  const enriched = L.rows;
  const usdt = L.closingUsdt;
  const shares = L.closingShares;
  const signup = data?.profile?.created_at;

  const terminalRows = enriched.filter(r => r.terminal).length;
  const wholeShares = Math.floor(shares);
  const fraction = +(shares - wholeShares).toFixed(4);

  return (
    <Section title="📜 Statement — Chronological Ledger (running balance)" totals={<>
      Movements: <strong style={{ color: '#FFF' }}>{enriched.length}</strong> ({terminalRows} terminal) ·
      Closing USDT (commission wallet): <strong style={{ color: signColor(usdt) }}>${fmt(usdt)}</strong> ·
      Closing equity shares (owned): <strong style={{ color: '#FFF' }}>{fmt(shares, 4)}</strong> <span style={lbl}>({wholeShares} whole + {fmt(fraction, 4)} fraction)</span>
      {(L.escrowUsdt > 0 || L.escrowShares > 0) ? <> · <span style={{ color: '#FCD34D' }}>Escrow (held): ${fmt(L.escrowUsdt)}{L.escrowShares > 0 ? ` · ${fmt(L.escrowShares, 4)} sh` : ''}</span></> : null}
    </>}>
      <div style={{ ...lbl, marginBottom: '8px' }}>Opening balance at signup ({dt(signup)}): USDT $0.00 · Shares 0.0000. Closing balances are anchored to source-of-truth (commission_balances); any residual from fees / rounding / manual corrections is shown as a Reconciliation adjustment row. Non-terminal rows are shown for transparency but do not move the balance. USDT is the commission wallet balance; shares are owned equity — the two buckets are tracked separately and never summed.</div>
      {enriched.length === 0 ? <Empty msg="No financial movement recorded for this user." /> : (
        <ScrollWrap>
          <table style={tableStyle}>
            <thead><tr>
              <th style={th}>When</th>
              <th style={th}>Type</th>
              <th style={th}>Description</th>
              <th style={th}>Counterparty</th>
              <th style={th}>Status</th>
              <th style={th}>USDT Δ</th>
              <th style={th}>Shares Δ</th>
              <th style={th}>USDT Balance</th>
              <th style={th}>Shares Balance</th>
              <th style={th}>Reference</th>
            </tr></thead>
            <tbody>
              {enriched.map(r => (
                <tr key={r.id} style={!r.terminal ? { opacity: 0.55 } : undefined}>
                  <td style={td}>{dt(r.when)}</td>
                  <td style={td}>{kindBadge(r.kind)}</td>
                  <td style={td}>{r.label}{r.note ? <div style={{ ...lbl, marginTop: '2px' }}>{r.note}</div> : null}</td>
                  <td style={td}>{r.counterparty || r.counterparty_fallback != null ? <CounterpartyCell user={r.counterparty} fallbackId={r.counterparty_fallback} /> : <span style={{ color: '#6B7280' }}>—</span>}</td>
                  <td style={td}><StatusPill status={r.status} /></td>
                  <td style={{ ...td, color: signColor(r.usdt_delta), fontWeight: 600 }}>{r.terminal ? `$${signed(r.usdt_delta)}` : '—'}</td>
                  <td style={{ ...td, color: signColor(r.shares_delta), fontWeight: 600 }}>{r.terminal ? signed(r.shares_delta, 4) : '—'}</td>
                  <td style={{ ...td, color: '#FFF', fontWeight: 600 }}>${fmt(r.usdt_balance)}</td>
                  <td style={{ ...td, color: '#FFF', fontWeight: 600 }}>{fmt(r.shares_balance, 4)}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px', color: '#9CA3AF' }}>{r.reference || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollWrap>
      )}
    </Section>
  );
}

function SharesTab({ data }: TabsProps) {
  const sp = data?.share_purchases || {};
  const st = data?.share_transfers || {};
  const cc = data?.commission_conversions || {};
  const purchases = sp.all || [];
  const conversions = cc.rows || [];
  const sent = st.sent || []; const received = st.received || [];

  // Canonical: only status='active' purchases count toward shares (mirrors my-shares.js).
  const terminalSp = (status: string) => norm(status) === 'active';
  const totalShares = useMemo(() => buildAccountLedger(data).closingShares, [data]);
  const whole = Math.floor(totalShares); const frac = +(totalShares - whole).toFixed(4);

  return (
    <>
      <Section title="🪙 Equity Shares (Owned) — Summary">
        <div style={{ ...lbl, marginBottom: '8px' }}>Equity shares (owned) is the single share total across purchases, approved conversions, legacy and net transfers. Commission shares earned are already counted here — they appear in the Commission Wallet (Commissions Earned) tab for reference and are never added twice.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
          <div><div style={lbl}>Equity shares (owned)</div><div style={{ color: '#FFF', fontSize: '20px', fontWeight: 700 }}>{fmt(totalShares, 4)}</div><div style={lbl}>{whole} whole · {fmt(frac, 4)} fraction</div></div>
          <div><div style={lbl}>Terminal purchases</div><div style={{ color: '#FFF', fontSize: '16px', fontWeight: 600 }}>{purchases.filter((p: any) => terminalSp(p.status)).length} / {purchases.length}</div></div>
          <div><div style={lbl}>Approved conversions</div><div style={{ color: '#FFF', fontSize: '16px', fontWeight: 600 }}>{conversions.filter((c: any) => norm(c.status) === 'approved').length} / {conversions.length}</div></div>
          <div><div style={lbl}>Completed transfers in</div><div style={{ color: '#FFF', fontSize: '16px', fontWeight: 600 }}>{received.filter((t: any) => norm(t.status) === 'completed').length} / {received.length}</div></div>
          <div><div style={lbl}>Completed transfers out</div><div style={{ color: '#FFF', fontSize: '16px', fontWeight: 600 }}>{sent.filter((t: any) => norm(t.status) === 'completed').length} / {sent.length}</div></div>
        </div>
      </Section>

      {purchases.length > 0 && (
        <Section title="🛒 Share Purchases" count={purchases.length}>
          <ScrollWrap><table style={tableStyle}><thead><tr>
            <th style={th}>When</th><th style={th}>Package</th><th style={th}>Shares</th><th style={th}>Bonus</th><th style={th}>Amount</th><th style={th}>Method</th><th style={th}>Status</th><th style={th}>Topup?</th><th style={th}>Reference</th>
          </tr></thead><tbody>
            {purchases.map((p: any) => (<tr key={p.id} style={!terminalSp(p.status) ? { opacity: 0.6 } : undefined}>
              <td style={td}>{dt(p.created_at)}</td>
              <td style={td}>{p.package_name || '—'}</td>
              <td style={td}>{fmt(p.shares_purchased, 4)}</td>
              <td style={td}>{fmt(p.bonus_shares, 4)}</td>
              <td style={td}>${fmt(p.total_amount)}</td>
              <td style={td}>{p.payment_method || '—'}</td>
              <td style={td}><StatusPill status={p.status} /></td>
              <td style={td}>{p.is_commission_topup ? 'YES' : ''}</td>
              <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px' }}>{p.transaction_reference || '—'}</td>
            </tr>))}
          </tbody></table></ScrollWrap>
        </Section>
      )}

      {conversions.length > 0 && (
        <Section title="🔄 Commission → Share Conversions" count={conversions.length}>
          <ScrollWrap><table style={tableStyle}><thead><tr>
            <th style={th}>When</th><th style={th}>Shares</th><th style={th}>USDT spent</th><th style={th}>Price</th><th style={th}>Phase</th><th style={th}>Status</th><th style={th}>Reason</th>
          </tr></thead><tbody>
            {conversions.map((r: any) => (<tr key={r.id} style={norm(r.status) !== 'approved' ? { opacity: 0.6 } : undefined}>
              <td style={td}>{dt(r.created_at)}</td>
              <td style={td}>{fmt(r.shares_requested, 4)}</td>
              <td style={td}>${fmt(r.usdt_amount)}</td>
              <td style={td}>${fmt(r.share_price)}</td>
              <td style={td}>{r.phase_number ?? r.phase_id ?? '—'}</td>
              <td style={td}><StatusPill status={r.status} /></td>
              <td style={td}>{r.rejection_reason || '—'}</td>
            </tr>))}
          </tbody></table></ScrollWrap>
        </Section>
      )}

      {(sent.length > 0 || received.length > 0) && (
        <Section title="🔁 Share Transfers" totals={<>Sent: <strong>{sent.length}</strong> · Received: <strong>{received.length}</strong></>}>
          {sent.length > 0 && <div style={{ marginBottom: '12px' }}>
            <div style={{ ...lbl, marginBottom: '4px' }}>Sent ({sent.length})</div>
            <ScrollWrap><table style={tableStyle}><thead><tr><th style={th}>When</th><th style={th}>Recipient</th><th style={th}>Shares</th><th style={th}>Reason</th><th style={th}>Status</th></tr></thead><tbody>
              {sent.map((t: any) => (<tr key={t.id} style={norm(t.status) !== 'completed' ? { opacity: 0.6 } : undefined}>
                <td style={td}>{dt(t.created_at)}</td>
                <td style={td}><CounterpartyCell user={t.counterparty_user} fallbackId={t.recipient_user_id} /></td>
                <td style={td}>{fmt(t.shares_transferred, 4)}</td>
                <td style={td}>{t.transfer_reason || '—'}</td>
                <td style={td}><StatusPill status={t.status} /></td>
              </tr>))}
            </tbody></table></ScrollWrap>
          </div>}
          {received.length > 0 && <div>
            <div style={{ ...lbl, marginBottom: '4px' }}>Received ({received.length})</div>
            <ScrollWrap><table style={tableStyle}><thead><tr><th style={th}>When</th><th style={th}>Sender</th><th style={th}>Shares</th><th style={th}>Reason</th><th style={th}>Status</th></tr></thead><tbody>
              {received.map((t: any) => (<tr key={t.id} style={norm(t.status) !== 'completed' ? { opacity: 0.6 } : undefined}>
                <td style={td}>{dt(t.created_at)}</td>
                <td style={td}><CounterpartyCell user={t.counterparty_user} fallbackId={t.sender_user_id} /></td>
                <td style={td}>{fmt(t.shares_transferred, 4)}</td>
                <td style={td}>{t.transfer_reason || '—'}</td>
                <td style={td}><StatusPill status={t.status} /></td>
              </tr>))}
            </tbody></table></ScrollWrap>
          </div>}
        </Section>
      )}
    </>
  );
}

function CommissionsEarnedTab({ data }: TabsProps) {
  const ce = data?.commissions_earned || {};
  const direct = ce.commission_transactions || [];
  const mlc = ce.multi_level_commissions || [];
  const t = ce.totals || {};
  return (
    <Section title="💰 Commission Wallet — Commissions Earned (from referrals)" totals={<>
      Direct rows: <strong>{direct.length}</strong> · Multi-level rows: <strong>{mlc.length}</strong> ·
      Gross: <strong style={{ color: '#FFF' }}>${fmt(t.gross_usdt_all_commission_records)}</strong> ·
      Paid: <strong style={{ color: '#86EFAC' }}>${fmt(t.gross_usdt_paid_status_only)}</strong> ·
      Unpaid: <strong style={{ color: '#FCD34D' }}>${fmt(t.gross_usdt_unpaid_status_in_pending_approved)}</strong>
    </>}>
      <div style={{ ...lbl, marginBottom: '8px' }}>USDT is the withdrawable commission wallet balance. Share commissions shown here are already counted in Equity Shares (Owned) — shown for reference and never added twice.</div>
      {direct.length > 0 && <div style={{ marginBottom: '12px' }}>
        <div style={{ ...lbl, marginBottom: '4px' }}>Direct commissions ({direct.length})</div>
        <ScrollWrap><table style={tableStyle}><thead><tr>
          <th style={th}>When</th><th style={th}>From (referred user)</th><th style={th}>Purchase</th><th style={th}>Rate</th><th style={th}>Purchase amount</th><th style={th}>USDT earned</th><th style={th}>Share comm</th><th style={th}>Type</th><th style={th}>Status</th>
        </tr></thead><tbody>
          {direct.map((c: any) => (<tr key={c.id} style={!TERMINAL.commission.has(norm(c.status)) ? { opacity: 0.6 } : undefined}>
            <td style={td}>{dt(c.created_at)}</td>
            <td style={td}><CounterpartyCell user={c.counterparty_user} fallbackId={c.referred_id} /></td>
            <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px' }}>#{c.share_purchase_id || '—'}</td>
            <td style={td}>{fmt(c.commission_rate)}</td>
            <td style={td}>${fmt(c.share_purchase_amount)}</td>
            <td style={{ ...td, color: '#86EFAC', fontWeight: 600 }}>${fmt(c.usdt_commission)}</td>
            <td style={td}>{fmt(c.share_commission, 4)}</td>
            <td style={td}>{c.transaction_type || '—'}</td>
            <td style={td}><StatusPill status={c.status} /></td>
          </tr>))}
        </tbody></table></ScrollWrap>
      </div>}
      {mlc.length > 0 && <div>
        <div style={{ ...lbl, marginBottom: '4px' }}>Multi-level commissions ({mlc.length})</div>
        <ScrollWrap><table style={tableStyle}><thead><tr>
          <th style={th}>When</th><th style={th}>From (referred user)</th><th style={th}>Original purchase</th><th style={th}>Level</th><th style={th}>Type</th><th style={th}>Purchase amount</th><th style={th}>Shares</th><th style={th}>USDT earned</th><th style={th}>Status</th>
        </tr></thead><tbody>
          {mlc.map((c: any) => (<tr key={c.id} style={!TERMINAL.commission.has(norm(c.status)) ? { opacity: 0.6 } : undefined}>
            <td style={td}>{dt(c.created_at)}</td>
            <td style={td}><CounterpartyCell user={c.counterparty_user} fallbackId={c.referred_id} /></td>
            <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px' }}>#{c.original_purchase_id || '—'}</td>
            <td style={td}>{c.level}</td>
            <td style={td}>{c.commission_type || '—'}</td>
            <td style={td}>${fmt(c.share_purchase_amount)}</td>
            <td style={td}>{fmt(c.shares_purchased, 4)}</td>
            <td style={{ ...td, color: '#86EFAC', fontWeight: 600 }}>${fmt(c.usdt_commission)}</td>
            <td style={td}><StatusPill status={c.status} /></td>
          </tr>))}
        </tbody></table></ScrollWrap>
      </div>}
    </Section>
  );
}

function CommissionsGeneratedTab({ data }: TabsProps) {
  const cpo = data?.commissions_paid_to_others || {};
  const direct = cpo.commission_transactions || [];
  const mlc = cpo.multi_level_commissions || [];
  const t = cpo.totals || {};
  return (
    <Section title="📤 Commissions Generated (paid to uplines from this user's purchases)" totals={<>
      Direct rows: <strong>{direct.length}</strong> · Multi-level rows: <strong>{mlc.length}</strong> ·
      Gross: <strong style={{ color: '#FFF' }}>${fmt(t.gross_usdt_all_paid_out_records)}</strong> ·
      Paid out: <strong style={{ color: '#FCD34D' }}>${fmt(t.gross_usdt_paid_status_only)}</strong>
    </>}>
      {direct.length > 0 && <div style={{ marginBottom: '12px' }}>
        <div style={{ ...lbl, marginBottom: '4px' }}>Direct commissions paid to upline ({direct.length})</div>
        <ScrollWrap><table style={tableStyle}><thead><tr>
          <th style={th}>When</th><th style={th}>To (upline)</th><th style={th}>Purchase</th><th style={th}>Rate</th><th style={th}>Purchase amount</th><th style={th}>USDT paid</th><th style={th}>Share comm</th><th style={th}>Type</th><th style={th}>Status</th>
        </tr></thead><tbody>
          {direct.map((c: any) => (<tr key={c.id} style={!TERMINAL.commission.has(norm(c.status)) ? { opacity: 0.6 } : undefined}>
            <td style={td}>{dt(c.created_at)}</td>
            <td style={td}><CounterpartyCell user={c.counterparty_user} fallbackId={c.referrer_id} /></td>
            <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px' }}>#{c.share_purchase_id || '—'}</td>
            <td style={td}>{fmt(c.commission_rate)}</td>
            <td style={td}>${fmt(c.share_purchase_amount)}</td>
            <td style={{ ...td, color: '#FCA5A5', fontWeight: 600 }}>${fmt(c.usdt_commission)}</td>
            <td style={td}>{fmt(c.share_commission, 4)}</td>
            <td style={td}>{c.transaction_type || '—'}</td>
            <td style={td}><StatusPill status={c.status} /></td>
          </tr>))}
        </tbody></table></ScrollWrap>
      </div>}
      {mlc.length > 0 && <div>
        <div style={{ ...lbl, marginBottom: '4px' }}>Multi-level commissions paid up the chain ({mlc.length})</div>
        <ScrollWrap><table style={tableStyle}><thead><tr>
          <th style={th}>When</th><th style={th}>To (upline)</th><th style={th}>Original purchase</th><th style={th}>Level</th><th style={th}>Type</th><th style={th}>Purchase amount</th><th style={th}>Shares</th><th style={th}>USDT paid</th><th style={th}>Status</th>
        </tr></thead><tbody>
          {mlc.map((c: any) => (<tr key={c.id} style={!TERMINAL.commission.has(norm(c.status)) ? { opacity: 0.6 } : undefined}>
            <td style={td}>{dt(c.created_at)}</td>
            <td style={td}><CounterpartyCell user={c.counterparty_user} fallbackId={c.referrer_id} /></td>
            <td style={{ ...td, fontFamily: 'monospace', fontSize: '10px' }}>#{c.original_purchase_id || '—'}</td>
            <td style={td}>{c.level}</td>
            <td style={td}>{c.commission_type || '—'}</td>
            <td style={td}>${fmt(c.share_purchase_amount)}</td>
            <td style={td}>{fmt(c.shares_purchased, 4)}</td>
            <td style={{ ...td, color: '#FCA5A5', fontWeight: 600 }}>${fmt(c.usdt_commission)}</td>
            <td style={td}><StatusPill status={c.status} /></td>
          </tr>))}
        </tbody></table></ScrollWrap>
      </div>}
    </Section>
  );
}

function ReferrerTab({ data }: TabsProps) {
  const r = data?.referrer;
  if (!r) return <Section title="🧭 Referrer (Upline)"><Empty msg="No referrer recorded for this user." /></Section>;
  return (
    <Section title="🧭 Referrer (Upline)">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
        <div>
          <div style={lbl}>Referrer</div>
          <div style={{ marginTop: '4px' }}><CounterpartyCell user={r.user} fallbackId={r.referrer_id} /></div>
        </div>
        <div><div style={lbl}>Referral code used</div><div style={{ color: '#FFF', fontFamily: 'monospace' }}>{r.referral_code || '—'}</div></div>
        <div><div style={lbl}>Linked at</div><div style={{ color: '#FFF' }}>{dt(r.created_at)}</div></div>
      </div>
      <div style={{ ...lbl, marginTop: '10px' }}>The Commissions Generated tab lists every commission paid up the chain — including this referrer — from this user's purchases.</div>
    </Section>
  );
}

export function FullFinancialAuditTabs({ data }: TabsProps) {
  const tabs: Array<{ key: TabKey; label: string; show: boolean }> = [
    { key: 'statement', label: '📜 Statement', show: true },
    { key: 'shares', label: '🪙 Shares', show: hasShares(data) },
    { key: 'earned', label: '💰 Commissions Earned', show: hasEarned(data) },
    { key: 'generated', label: '📤 Commissions Generated', show: hasGenerated(data) },
    { key: 'referrer', label: '🧭 Referrer', show: hasReferrer(data) },
  ].filter(t => t.show) as any;
  const [active, setActive] = useState<TabKey>('statement');
  const current = tabs.find(t => t.key === active) ? active : tabs[0]?.key;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', borderBottom: '1px solid #374151', marginBottom: '12px', paddingBottom: '4px' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActive(t.key)} style={{
            backgroundColor: current === t.key ? '#F59E0B' : '#1F2937',
            color: current === t.key ? '#111827' : '#E5E7EB',
            border: 'none', borderRadius: '6px 6px 0 0', padding: '8px 14px',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
          }}>{t.label}</button>
        ))}
      </div>
      {current === 'statement' && <StatementTab data={data} />}
      {current === 'shares' && <SharesTab data={data} />}
      {current === 'earned' && <CommissionsEarnedTab data={data} />}
      {current === 'generated' && <CommissionsGeneratedTab data={data} />}
      {current === 'referrer' && <ReferrerTab data={data} />}
    </div>
  );
}
